import React, { useState, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { authFlowFlags } from '../lib/authFlowFlags';
import { auth } from '../config/firebase';
import './Auth.css';

function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [timezone, setTimezone] = useState<string>('');
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const SMS_CONSENT_TEXT =
    'By checking this box, I agree to receive recurring SMS messages from Mosaime Wellness Assistant ' +
    'at the phone number provided. Message frequency varies based on your check-ins. ' +
    'Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for help. ' +
    'Consent is not a condition of purchase.';

  // Track whether a signup submission is in-flight so redirects don't interfere
  const submittingRef = useRef(false);

  // Redirect already-authenticated users unless a signup is in progress
  if (!isLoading && isAuthenticated && !submittingRef.current) {
    return <Navigate to="/settings" replace />;
  }

  // Helpers to validate/format US phone numbers
  const digitsOnly = (s: string) => s.replace(/\D/g, '');

  const formatUS = (digits: string) => {
    let d = digits;
    if (d.startsWith('1') && d.length > 10) d = d.slice(1);
    const core = d.slice(0, 10);
    if (core.length === 0) return '';
    if (core.length <= 3) return core;
    if (core.length <= 6) return `(${core.slice(0, 3)}) ${core.slice(3)}`;
    return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value || '';
    const d = digitsOnly(raw);
    const formatted = formatUS(d);
    setPhoneNumber(formatted);

    let core = d;
    if (core.length === 11 && core.startsWith('1')) core = core.slice(1);
    if (core.length === 10) {
      setIsPhoneValid(true);
      setPhoneError('');
    } else {
      setIsPhoneValid(false);
      if (d.length > 0 && core.length !== 10) {
        setPhoneError('US phone numbers must be 10 digits (area code + number)');
      } else {
        setPhoneError('');
      }
    }
  };

  const handlePhoneBlur = () => {
    const d = digitsOnly(phoneNumber);
    const formatted = formatUS(d);
    setPhoneNumber(formatted);
  };

  const availableTimezones = [
    { value: 'America/New_York', label: 'Eastern (US & Canada)' },
    { value: 'America/Chicago', label: 'Central (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain (US & Canada)' },
    { value: 'America/Phoenix', label: 'Mountain (Arizona - no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific (US & Canada)' },
    { value: 'America/Anchorage', label: 'Alaska' },
    { value: 'Pacific/Honolulu', label: 'Hawaii' },
  ];

  // default timezone to user's browser tz if available, otherwise Eastern
  React.useEffect(() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected && availableTimezones.some(t => t.value === detected)) {
        setTimezone(detected);
      } else if (!timezone) {
        setTimezone('America/New_York');
      }
    } catch (e) {
      if (!timezone) setTimezone('America/New_York');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = () => {
    authFlowFlags.signupInProgress = false;
    setIsSubmitting(false);
    submittingRef.current = false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError('');
    setPhoneError('');
    setIsSubmitting(true);
    submittingRef.current = true;

    // Signal to AuthContext that a coordinated signup is in progress
    authFlowFlags.signupInProgress = true;

    // Basic validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      cleanup();
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      cleanup();
      return;
    }

    if (!isPhoneValid) {
      setPhoneError('Please enter a valid US phone number (e.g. (555) 555-5555)');
      cleanup();
      return;
    }

    if (!termsAccepted) {
      setError('You must agree to the Terms & Conditions');
      cleanup();
      return;
    }

    if (!smsOptIn) {
      setError('You must consent to receive SMS messages to use this service');
      cleanup();
      return;
    }

    try {
      // send phone in E.164 format (US only)
      const rawDigits = digitsOnly(phoneNumber);
      let core = rawDigits;
      if (core.length === 11 && core.startsWith('1')) core = core.slice(1);
      const e164 = `+1${core}`;

      // API base: can be overridden with VITE_API_BASE to point to Cloud Run
      const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';

      // Ensure Firebase is configured
      if (!auth) {
        setError('Firebase is not configured in this environment.');
        cleanup();
        return;
      }

      // 1) Create Firebase user on the client
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(auth, email, password);
      } catch (fbErr: any) {
        const msg = fbErr?.message || 'Failed to create account';
        setError(msg);
        cleanup();
        return;
      }

      // 2) Update display name locally
      try {
        await updateProfile(userCredential.user, { displayName: name });
      } catch (updErr) {
        console.warn('Failed to update Firebase profile displayName', updErr);
      }

      // 3) Get a fresh ID token for backend calls
      const idToken = await userCredential.user.getIdToken(true);

      // 4) Try to upsert into the backend (requires ID token)
      let signupToken: string | null = null;
      try {
        const upsertRes = await fetch(`${API_BASE}/auth/upsert-social`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            name,
            email,
            phoneNumber: e164,
            timezone,
            smsOptIn: true,
            smsConsentText: SMS_CONSENT_TEXT,
          }),
        });

        const upsertData = await upsertRes.json().catch(() => ({}));
        if (!upsertRes.ok) {
          if (upsertRes.status === 409) {
            setError(upsertData?.error || 'An account with these details already exists');
            cleanup();
            return;
          }
          throw new Error('Upsert failed');
        }

        signupToken = upsertData?.data?.signupToken || null;
      } catch (e) {
        console.error('[signup] upsert-social error:', e);
      }

      // 5) Fallback: call public signup route to create a pending user and obtain signupToken
      if (!signupToken) {
        try {
          const publicRes = await fetch(`${API_BASE}/signup/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name,
              email,
              phoneNumber: e164,
              timezone,
              smsOptIn: true,
              smsConsentText: SMS_CONSENT_TEXT,
            }),
          });
          const publicData = await publicRes.json().catch(() => ({}));
          if (publicRes.status === 409) {
            setError(publicData?.error || 'An account with these details already exists');
            cleanup();
            return;
          }
          if (publicRes.ok) {
            signupToken = publicData?.data?.signupToken || null;
          }
        } catch (pubErr) {
          console.error('[signup] fallback error:', pubErr);
        }
      }

      if (!signupToken) {
        setError('Failed to create account on server. Please try again.');
        cleanup();
        return;
      }

      // Navigate FIRST so Signup unmounts before isAuthenticated can become true,
      // then clear the flag so AuthContext can proceed with /auth/me.
      navigate(`/checkout?t=${encodeURIComponent(signupToken)}`);
      authFlowFlags.signupInProgress = false;
      // Do not call cleanup() here; we want to keep submittingRef true until navigation completes.
    } catch (err) {
      console.error('Signup error (frontend):', err);
      setError('Failed to create account. Please try again.');
      cleanup();
    }
  };

  // Social login handlers removed because social buttons are temporarily hidden

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Link to="/" className="auth-logo">
          <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img" />
          <span className="logo-text">mosaime</span>
        </Link>

        <div className="auth-card">
          <h1>Create Account</h1>
          <p className="auth-subtitle">Start your wellness journey today</p>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                value={phoneNumber}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                placeholder="(555) 000-0000"
                aria-invalid={!isPhoneValid && phoneNumber.length > 0}
                required
              />
              <span className="help-text">Used for SMS chatbot interactions</span>
              {phoneError && <div className="auth-field-error">{phoneError}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="timezone">Timezone *</label>
              <select id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} required>
                {availableTimezones.map(t => (
                  <option key={t.value} value={t.value}>{t.label} — {t.value}</option>
                ))}
              </select>
              <span className="help-text">Select the timezone for your account (used for scheduling)</span>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>

            <div className="terms-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>I agree to the{' '}
                <a
                  href="/terms"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowTermsModal(true);
                  }}
                >
                  Terms &amp; Conditions
                </a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowPrivacyModal(true);
                  }}
                >
                  Privacy Policy
                </a></span>
              </label>
            </div>

            <div className="terms-checkbox sms-consent-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                />
                <span className="sms-consent-text">
                  I agree to receive recurring SMS messages from <strong>Mosaime Wellness Assistant</strong> at
                  the phone number provided. Message frequency varies based on your check‑ins.
                  Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for help.
                  Consent is not a condition of purchase.
                </span>
              </label>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={isLoading || isSubmitting || !isPhoneValid || !termsAccepted || !smsOptIn}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {showTermsModal && (
            <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
              <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="terms-modal-header">
                  <h2>Terms &amp; Conditions</h2>
                  <button className="terms-modal-close" onClick={() => setShowTermsModal(false)} aria-label="Close">&times;</button>
                </div>
                <div className="terms-modal-body">
                  <p><strong>Mosaime LLC – Terms &amp; Conditions</strong></p>
                  <p>Last Updated: March 26, 2026</p>
                  <p>Welcome to Mosaime LLC (&ldquo;Mosaime,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of our SMS&#8209;based wellness companion service and any related websites, applications, or content (collectively, the &ldquo;Service&rdquo;). By signing up or using the Service, you agree to these Terms.</p>

                  <h3>1. SMS Messaging Consent</h3>
                  <p>By creating an account or providing your phone number, you consent to receive automated SMS messages from Mosaime. Message frequency varies based on your usage and settings. Message and data rates may apply. Consent to receive SMS messages is not a condition of purchase.</p>
                  <p>You may opt out at any time by texting STOP. For help, contact us at contact@mosaime.com.</p>

                  <h3>2. Wellness&#8209;Only, Non&#8209;Medical Service</h3>
                  <ul>
                    <li>Mosaime provides general wellness guidance and lifestyle support.</li>
                    <li>We do not provide medical advice, diagnosis, treatment, or therapy.</li>
                    <li>The Service is not a substitute for professional medical care.</li>
                    <li>Always consult a qualified healthcare provider for medical concerns.</li>
                  </ul>

                  <h3>3. User Responsibilities</h3>
                  <p>You agree not to use the Service to send harmful, abusive, or unlawful content. You are responsible for maintaining the confidentiality of your account and for all activity under your account.</p>

                  <h3>4. Privacy and Data</h3>
                  <ul>
                    <li>By using the Service, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</li>
                    <li>SMS is not a secure communication channel. Do not send sensitive information such as medical details, financial information, or passwords.</li>
                    <li>We may collect and store information you provide, including message content, usage data, and device information, to operate and improve the Service.</li>
                  </ul>

                  <h3>5. Fitbit Integration</h3>
                  <p>By connecting your Fitbit account to Mosaime, you authorize us to access and use the data that Fitbit makes available through its API. This data may include activity metrics, sleep information, heart rate data, and other wellness&#8209;related information.</p>

                  <h4>a. User Responsibilities</h4>
                  <p>By connecting your Fitbit account, you confirm that:</p>
                  <ul>
                    <li>You have the right to connect your Fitbit account and authorize this access.</li>
                    <li>The information provided through Fitbit is accurate to the best of your knowledge.</li>
                    <li>You will comply with Fitbit&rsquo;s own terms of service and policies.</li>
                  </ul>

                  <h4>b. Use of Fitbit Data for Recommendations</h4>
                  <p>You acknowledge that Mosaime may analyze your Fitbit data using automated systems, including AI models, to generate personalized recommendations, reminders, and insights. These recommendations are informational only and are not medical advice.</p>

                  <h4>c. Data Retention</h4>
                  <p>We retain Fitbit data as long as your account is active or as needed to provide the service. You may request deletion of your Fitbit data at any time by contacting us.</p>

                  <h4>d. Revoking Access</h4>
                  <p>You may disconnect your Fitbit account at any time through your Mosaime settings or through your Fitbit account settings. Once disconnected, we will stop receiving new data from Fitbit.</p>

                  <h4>e. No Medical Advice</h4>
                  <p>Fitbit data is used solely for general wellness and habit&#8209;support purposes. Mosaime is not a medical service and does not provide medical diagnosis, treatment, or advice.</p>

                  <h3>6. Service Availability</h3>
                  <p>The Service may be modified, suspended, or discontinued at any time without notice. We are not responsible for delays or failures in SMS delivery caused by carriers or third&#8209;party providers.</p>

                  <h3>7. Disclaimers</h3>
                  <p>The Service is provided &ldquo;as is&rdquo; without warranties of any kind. We do not guarantee accuracy, completeness, or results from using the Service.</p>

                  <h3>8. Limitation of Liability</h3>
                  <p>To the fullest extent permitted by law, Mosaime LLC is not liable for any indirect, incidental, consequential, or punitive damages arising from your use of the Service.</p>

                  <h3>9. Governing Law</h3>
                  <p>These Terms are governed by the laws of the State of New Jersey, without regard to conflict&#8209;of&#8209;law principles.</p>

                  <h3>10. Changes to Terms</h3>
                  <p>We may update these Terms at any time. Continued use of the Service constitutes acceptance of the updated Terms.</p>

                  <h3>11. Contact</h3>
                  <p>For questions or support, contact:<br />Mosaime LLC<br />contact@mosaime.com</p>
                </div>
                <div className="terms-modal-footer">
                  <button className="btn btn-primary" onClick={() => setShowTermsModal(false)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {showPrivacyModal && (
            <div className="terms-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
              <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
                <div className="terms-modal-header">
                  <h2>Privacy Policy</h2>
                  <button className="terms-modal-close" onClick={() => setShowPrivacyModal(false)} aria-label="Close">&times;</button>
                </div>
                <div className="terms-modal-body">
                  <p><strong>Mosaime Privacy Policy</strong></p>
                  <p>Last Updated: March 26, 2026</p>

                  <h3>Introduction</h3>
                  <p>Welcome to Mosaime, your AI&#8209;powered wellness assistant. At Mosaime (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), we are committed to protecting your privacy and handling your personal information with care. This Privacy Policy explains how we collect, use, share, and protect your information when you use our wellness guidance services via SMS and web. It also outlines your rights and choices regarding your data. Our approach is privacy&#8209;first: we collect only what we need, never sell your data, and give you clear control over your information.</p>
                  <p>This policy is designed for U.S.&#8209;based users and is written in plain English to be accessible and transparent. It is also tailored to comply with U.S. federal and state privacy laws, industry guidelines, and SMS carrier requirements, including those set by Twilio for A2P 10DLC and toll&#8209;free messaging verification.</p>
                  <p>By using Mosaime&rsquo;s services, you agree to the practices described in this Privacy Policy. If you have any questions, please contact us using the details at the end of this policy.</p>

                  <h3>1. What Data We Collect</h3>
                  <p>We collect only the information necessary to provide and improve our wellness assistant services.</p>

                  <h4>a. Information You Provide Directly</h4>
                  <ul>
                    <li><strong>Phone Number:</strong> Required to send and receive SMS wellness guidance.</li>
                    <li><strong>Messages:</strong> Content of SMS or web messages you send to or receive from Mosaime, including your responses, questions, and feedback.</li>
                    <li><strong>Web Form Data:</strong> Information you enter when signing up or interacting with Mosaime on the web, such as your name (optional), preferences, or wellness goals.</li>
                    <li><strong>Consent Records:</strong> Your opt&#8209;in/opt&#8209;out choices, including timestamps and method of consent (e.g., web form, SMS keyword).</li>
                  </ul>

                  <h4>b. Information Collected Automatically</h4>
                  <ul>
                    <li><strong>Usage Data:</strong> Details about your interactions with Mosaime, such as message delivery status, timestamps, message frequency, and engagement metrics.</li>
                    <li><strong>Device and Technical Data:</strong> When you use our web interface, we may collect device type, browser type, operating system, and IP address for security and troubleshooting.</li>
                    <li><strong>Cookies and Similar Technologies:</strong> Our website may use cookies or similar tools to support core functionality and analytics. We do not use cookies for cross&#8209;site tracking or advertising.</li>
                  </ul>

                  <h4>c. Sensitive and Health&#8209;Related Data</h4>
                  <ul>
                    <li><strong>Wellness Information:</strong> If you choose to share information about your habits, routines, or wellness goals, this may be considered sensitive. Mosaime is not a medical device and does not collect or process protected health information (PHI) as defined by HIPAA. We do not request or require medical diagnoses, insurance numbers, or other regulated health data.</li>
                  </ul>

                  <h4>d. Data from Third Parties</h4>
                  <ul>
                    <li><strong>Service Providers:</strong> We may receive limited data from third&#8209;party processors (e.g., Twilio) necessary to deliver SMS messages, such as delivery status or error codes.</li>
                  </ul>

                  <h4>e. Fitbit Integration Data</h4>
                  <ul>
                    <li><strong>Fitbit Account Data:</strong> If you choose to connect your Fitbit account to Mosaime, we receive data that Fitbit makes available through its API after you authorize the connection. This may include activity metrics (such as steps and exercise), heart rate data, sleep information, and other wellness&#8209;related information.</li>
                  </ul>

                  <p>We do not collect or store Social Security numbers, payment information, government IDs, or precise geolocation.</p>

                  <h3>2. How We Use Your Data</h3>

                  <h4>a. Service Delivery</h4>
                  <ul>
                    <li><strong>Wellness Guidance:</strong> To send you personalized wellness tips, reminders, and motivational messages via SMS and web.</li>
                    <li><strong>User Support:</strong> To respond to your questions, requests, or feedback.</li>
                  </ul>

                  <h4>b. Service Improvement</h4>
                  <ul>
                    <li><strong>Analytics:</strong> To analyze usage patterns, message delivery, and engagement to improve our services, troubleshoot issues, and enhance user experience.</li>
                    <li><strong>AI Model Enhancement:</strong> We may use de&#8209;identified and aggregated message data to improve our AI&rsquo;s ability to provide relevant and effective guidance. We do not use your identifiable messages for external AI training or third&#8209;party model development without your explicit consent.</li>
                  </ul>

                  <h4>c. Compliance and Security</h4>
                  <ul>
                    <li><strong>Consent Management:</strong> To document and honor your opt&#8209;in/opt&#8209;out choices as required by law and carrier policies.</li>
                    <li><strong>Fraud Prevention:</strong> To detect and prevent spam, abuse, or unauthorized access.</li>
                    <li><strong>Legal Obligations:</strong> To comply with applicable laws, regulations, and carrier requirements (e.g., TCPA, CTIA, CCPA/CPRA).</li>
                  </ul>

                  <h4>d. Communication</h4>
                  <ul>
                    <li><strong>Transactional Notices:</strong> To notify you of changes to our services, privacy policy updates, or important account information.</li>
                    <li><strong>Support and Help:</strong> To provide assistance if you contact us via email.</li>
                  </ul>

                  <h4>e. Fitbit Data</h4>
                  <p>If you connect your Fitbit account, we use that data to:</p>
                  <ul>
                    <li><strong>Personalized Insights:</strong> Provide personalized insights, recommendations, and reminders based on your activity, sleep, and wellness patterns.</li>
                    <li><strong>Coaching Accuracy:</strong> Enhance the accuracy and relevance of your coaching experience.</li>
                    <li><strong>Product Improvement:</strong> Improve our product and develop new features based on aggregated, de&#8209;identified trends.</li>
                    <li><strong>AI Analysis:</strong> Analyze your data using automated systems, including AI models, to generate personalized guidance. These models process your data but do not use it to train external systems.</li>
                  </ul>

                  <p>We do not use your data for third&#8209;party marketing, advertising, or profiling.</p>

                  <h3>3. Data Sharing Practices</h3>
                  <p>Mosaime is committed to a strict privacy&#8209;first approach. We do not sell, rent, or share your personal information with third parties for their own marketing or promotional purposes.</p>

                  <h4>a. Third&#8209;Party Service Providers</h4>
                  <p>We may share your data with trusted third&#8209;party processors only as necessary to operate our services, including:</p>
                  <ul>
                    <li><strong>SMS Delivery Providers:</strong> Such as Twilio, to send and receive SMS messages.</li>
                    <li><strong>Cloud Hosting and IT Providers:</strong> For secure storage and processing of data.</li>
                    <li><strong>Customer Support Tools:</strong> To manage support requests and communications.</li>
                  </ul>
                  <p>All service providers are contractually required to protect your data, use it only for the specified purpose, and comply with applicable privacy laws and security standards.</p>

                  <h4>b. Legal and Regulatory Disclosures</h4>
                  <p>We may disclose your information if required by law, regulation, legal process, or governmental request, or to protect the rights, property, or safety of Mosaime, our users, or others.</p>

                  <h4>c. Business Transfers</h4>
                  <p>If Mosaime is involved in a merger, acquisition, or asset sale, your information may be transferred as part of that transaction. We will notify you of any such change and your choices regarding your data.</p>

                  <h4>d. No Sale or Unauthorized Sharing</h4>
                  <p>We do not sell, rent, or otherwise disclose your personal data to advertisers, data brokers, or unrelated third parties. SMS opt&#8209;in data and consent records are never shared for marketing or promotional purposes.</p>

                  <h4>e. Fitbit Data Sharing</h4>
                  <p>We only share your Fitbit data with:</p>
                  <ul>
                    <li><strong>Service Providers:</strong> Cloud hosting and infrastructure providers who help us operate the platform, subject to the same data protection requirements described above.</li>
                    <li><strong>Fitbit:</strong> As required by Fitbit&rsquo;s API terms and conditions.</li>
                    <li><strong>Other Parties:</strong> Only with your explicit consent or as required by law.</li>
                  </ul>
                  <p>We do not share your Fitbit data with advertisers or data brokers.</p>

                  <h3>4. Data Retention and Deletion</h3>
                  <p>We retain your personal data only as long as necessary to fulfill the purposes described in this policy, comply with legal obligations, resolve disputes, and enforce our agreements.</p>

                  <h4>a. Retention Periods</h4>
                  <ul>
                    <li><strong>Active Users:</strong> We retain your phone number, message history, and usage data while your account is active and you are subscribed to our services.</li>
                    <li><strong>Opt&#8209;Out or Account Deletion:</strong> If you opt out of SMS messages (by replying &ldquo;STOP&rdquo;), we will immediately stop sending messages and add your number to our opt&#8209;out list. If you request account deletion, we will remove your phone number from active use and delete or anonymize personal information as required by applicable law.</li>
                  </ul>

                  <h4>b. SMS and Message Data</h4>
                  <ul>
                    <li><strong>Consent Records:</strong> Opt&#8209;in/opt&#8209;out records are retained for at least four years to comply with TCPA and carrier requirements.</li>
                  </ul>

                  <h4>c. Data Deletion Requests</h4>
                  <p>You may request deletion of your personal data at any time. We will honor your request unless we are required to retain certain information for legal, regulatory, or security reasons.</p>

                  <h4>d. Backups and Residual Copies</h4>
                  <p>Deleted data may persist in backups for up to 60 days but will not be used for any active purpose and will be securely deleted in accordance with our data retention schedule.</p>

                  <h4>e. Fitbit Data</h4>
                  <p>We retain Fitbit data as long as your account is active or as needed to provide the service. If you disconnect your Fitbit account or delete your Mosaime account, you may request deletion of your previously collected Fitbit data. See &ldquo;Your Rights&rdquo; below for how to submit a request.</p>

                  <h3>5. Your Rights and Choices</h3>
                  <p>We believe you should have clear control over your information. Depending on where you live, you may have the following rights:</p>

                  <h4>a. Access</h4>
                  <p>You can request a copy of the personal data we hold about you, including message history, consent records, and usage data.</p>

                  <h4>b. Correction</h4>
                  <p>You may request correction of inaccurate or incomplete information.</p>

                  <h4>c. Deletion</h4>
                  <p>You can request deletion of your personal data (&ldquo;right to be forgotten&rdquo;). We will delete your data unless retention is required by law or for legitimate business purposes.</p>

                  <h4>d. Opt&#8209;Out</h4>
                  <p>Reply &ldquo;STOP&rdquo; to any Mosaime SMS to immediately opt out of further messages. You may also contact us to opt out via our website or via email.</p>

                  <h4>e. Data Portability</h4>
                  <p>You may request a copy of your data in a structured, commonly used, and machine&#8209;readable format.</p>

                  <h4>f. Restriction and Objection</h4>
                  <p>You may request that we restrict or stop processing your data in certain circumstances.</p>

                  <h4>g. Automated Decision&#8209;Making</h4>
                  <p>Mosaime&rsquo;s AI provides guidance and recommendations, but does not make legally binding or consequential automated decisions about you. If you have concerns about AI&#8209;driven outputs, you may contact us to request human review or clarification.</p>

                  <h4>h. How to Exercise Your Rights</h4>
                  <p>To exercise any of these rights, please contact us using the information in the &ldquo;Contact Us&rdquo; section. We may need to verify your identity before fulfilling your request, as required by law.</p>

                  <h4>i. Revoking Fitbit Access</h4>
                  <p>You may disconnect your Fitbit account at any time through your Mosaime account settings or through your Fitbit account settings. Once disconnected, we will stop receiving new data from Fitbit. You may also request deletion of previously collected Fitbit data by contacting us.</p>

                  <h3>6. Security Practices</h3>
                  <p>We take the security of your information seriously and use reasonable technical and organizational measures to protect it from unauthorized access, loss, or misuse.</p>
                  <ul>
                    <li><strong>Encryption:</strong> We use encryption to protect your information during transmission and when stored on our systems.</li>
                    <li><strong>Access Controls:</strong> Access to personal information is limited to authorized personnel who need it to operate and improve the service.</li>
                    <li><strong>Service Providers:</strong> We work with trusted third&#8209;party providers and require them to protect your information in accordance with applicable laws.</li>
                    <li><strong>Security Monitoring:</strong> We monitor our systems for potential vulnerabilities and take steps to maintain the security and reliability of our service.</li>
                    <li><strong>Incident Response:</strong> If a data breach affects your personal information, we will notify you as required by applicable law.</li>
                  </ul>

                  <h3>7. Children&rsquo;s Privacy</h3>
                  <p>Mosaime is intended for use by individuals aged 18 and older. We do not knowingly collect or solicit personal information from children under 13 (or under 16 in certain states). If we learn that we have collected personal information from a child without parental consent, we will promptly delete that information.</p>

                  <h3>8. AI and Automated Processing Disclosures</h3>

                  <h4>a. How AI Is Used</h4>
                  <ul>
                    <li><strong>Operational Use:</strong> AI processes your messages and responses to provide tailored wellness tips and reminders.</li>
                    <li><strong>Fitbit Data Analysis:</strong> If you connect your Fitbit account, AI models also analyze your Fitbit data to generate personalized wellness insights, activity recommendations, and reminders. These models process your data but do not use it to train external systems.</li>
                    <li><strong>Model Improvement:</strong> We may use de&#8209;identified and aggregated data to improve our AI models. We do not use your identifiable data for external AI training or share it with third parties for their own AI development without your explicit consent.</li>
                  </ul>

                  <h4>b. Automated Decision&#8209;Making</h4>
                  <ul>
                    <li><strong>No Legal or Significant Effects:</strong> Mosaime&rsquo;s AI does not make decisions that have legal or similarly significant effects on you. All guidance is informational and intended to support your wellness journey.</li>
                    <li><strong>Human Review:</strong> If you have concerns about an AI&#8209;generated recommendation, you may request human review or clarification.</li>
                  </ul>

                  <h3>9. Data De&#8209;Identification and Anonymization</h3>
                  <p>Where possible, we use de&#8209;identification and anonymization techniques to protect your privacy.</p>
                  <ul>
                    <li><strong>De&#8209;Identification:</strong> Personal identifiers (such as phone numbers) are removed or replaced with pseudonyms before data is used for analytics or AI training.</li>
                    <li><strong>No Re&#8209;Identification:</strong> We do not attempt to re&#8209;identify de&#8209;identified data, and our contracts with service providers prohibit such actions.</li>
                  </ul>

                  <h3>10. Data Sharing, Processors, and International Transfers</h3>

                  <h4>a. Data Processors</h4>
                  <p>We use trusted third&#8209;party processors (such as Twilio) to deliver SMS and manage technical infrastructure. These processors are contractually required to use your data only as instructed by Mosaime, implement strong security measures, notify us promptly of any data breaches, and assist with data subject rights requests.</p>

                  <h4>b. International Transfers</h4>
                  <p>Mosaime is based in the United States and primarily serves U.S. users. If your data is transferred outside the U.S., we ensure that appropriate safeguards are in place, as required by law.</p>

                  <h3>11. Compliance with Laws and Industry Standards</h3>
                  <p>Mosaime complies with all applicable U.S. federal and state privacy laws, including but not limited to:</p>
                  <ul>
                    <li><strong>Telephone Consumer Protection Act (TCPA):</strong> We obtain your express consent before sending SMS messages and honor all opt&#8209;out requests promptly.</li>
                    <li><strong>California Consumer Privacy Act (CCPA/CPRA):</strong> California residents have additional rights regarding their personal information, including the right to know, delete, correct, and opt out of sale or sharing. We do not sell or share your personal information for cross&#8209;context behavioral advertising.</li>
                    <li><strong>CTIA and Carrier Guidelines:</strong> We follow CTIA Messaging Principles and Best Practices, including clear opt&#8209;in/opt&#8209;out mechanisms, privacy policy disclosures, and content restrictions.</li>
                    <li><strong>HIPAA:</strong> Mosaime is not a covered entity under HIPAA and does not process protected health information (PHI).</li>
                  </ul>

                  <h3>12. SMS Consent, Opt&#8209;In, and Opt&#8209;Out</h3>

                  <h4>a. Consent</h4>
                  <p>You must provide clear, documented consent before receiving SMS messages from Mosaime. Consent is not a condition of purchase.</p>

                  <h4>b. Opt&#8209;Out</h4>
                  <p>You can opt out of SMS messages at any time by replying &ldquo;STOP&rdquo; to any message. We will confirm your opt&#8209;out and immediately cease sending further messages unless you opt in again.</p>

                  <h4>c. Recordkeeping</h4>
                  <p>We maintain records of your consent, including timestamp, method, and disclosure language, for at least four years to comply with legal and carrier requirements.</p>

                  <h3>13. Data Breach Notification</h3>
                  <p>If a data breach affects your personal information, we will notify you as required by applicable law.</p>

                  <h3>14. Changes to This Privacy Policy</h3>
                  <p>We may update this Privacy Policy from time to time. If we make material changes, we will notify you via SMS, web notification, or email (if provided). The &ldquo;Last Updated&rdquo; date at the top of this policy indicates when it was most recently revised.</p>

                  <h3>15. Contact Us</h3>
                  <p>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
                  <p>Mosaime Team<br />Email: contact@mosaime.com<br />Mail: 28 Spring Street #5133, Princeton, NJ 08542</p>

                  <h3>16. Additional Disclaimers</h3>
                  <ul>
                    <li><strong>Wellness Guidance Only:</strong> Mosaime provides general wellness tips and motivational messages. Our service is not a substitute for professional medical advice, diagnosis, or treatment.</li>
                    <li><strong>No Emergency Services:</strong> Do not use Mosaime for emergency or crisis situations. If you are experiencing a medical emergency, call 911 or seek immediate assistance.</li>
                  </ul>

                  <h3>17. Effective Date</h3>
                  <p>This Privacy Policy is effective as of March 26, 2026.</p>

                  <p>Thank you for trusting Mosaime with your wellness journey. Your privacy is our priority.</p>
                </div>
                <div className="terms-modal-footer">
                  <button className="btn btn-primary" onClick={() => setShowPrivacyModal(false)}>Close</button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default Signup;